'use client'

import type { SortDirection } from '@tanstack/react-table'

import * as React from 'react'

import { cn } from '../../utils/cn'
import { FLUID_COLUMN } from '../../utils/grids'
import { Skeleton } from './Skeleton'

type TableProps = React.ComponentProps<'div'> & {
  /**
   * Optional CSS grid-template-columns value (e.g. 'auto 1fr auto').
   * Applied to all rows via a CSS variable so header and body align.
   */
  gridTemplateColumns?: string
  /**
   * When provided, and `columns` is not, the grid template will be computed as
   * `repeat(columnCount, minmax(0, 1fr))` so that all columns are even.
   */
  columnCount?: number
  /**
   * Minimum table width in pixels. When set, triggers horizontal scroll
   * if the container is narrower than this value.
   */
  minWidth?: number
  /**
   * `visible` drops the built-in `overflow-x-auto` container. Any overflow value other than
   * `visible` makes the container a scroll container, which traps `position: sticky` descendants
   * inside it — pass `visible` when they must stick to an ancestor scroller instead; the caller
   * then owns horizontal overflow.
   */
  overflowX?: 'auto' | 'visible'
}

type TableStyle = React.CSSProperties & { ['--table-columns']?: string }

const Table = React.forwardRef<HTMLDivElement, TableProps>(function Table(
  { className, style, gridTemplateColumns, columnCount, minWidth, overflowX = 'auto', ...props },
  ref
) {
  const gridTemplateColumnsFallback =
    columnCount && columnCount > 0 ? `repeat(${columnCount}, ${FLUID_COLUMN})` : '1fr'

  const styleWithColumns: TableStyle = {
    ...(style as React.CSSProperties),
    ['--table-columns']: gridTemplateColumns ?? gridTemplateColumnsFallback
  }

  // When minWidth is set, put border on scroll container to eliminate dead zones
  if (minWidth) {
    return (
      <div
        data-slot="table-container"
        className={cn(
          'relative min-h-0 w-full rounded-lg border',
          overflowX === 'auto' && 'overflow-x-auto'
        )}
      >
        <div
          data-slot="table"
          role="table"
          className={cn('flex min-h-0 w-full flex-col text-sm', className)}
          style={{ ...styleWithColumns, minWidth }}
          ref={ref}
          {...props}
        />
      </div>
    )
  }

  return (
    <div
      data-slot="table-container"
      className={cn('relative h-full min-h-0 w-full', overflowX === 'auto' && 'overflow-x-auto')}
    >
      <div
        data-slot="table"
        role="table"
        className={cn(
          'flex min-h-0 w-full caption-bottom flex-col rounded-lg border text-sm',
          className
        )}
        style={styleWithColumns}
        ref={ref}
        {...props}
      />
    </div>
  )
})

function TableHeader({
  className,
  sticky,
  style,
  ...props
}: React.ComponentProps<'div'> & {
  sticky?: boolean
}) {
  return (
    <div
      data-slot="table-header"
      role="rowgroup"
      className={cn(
        // Opaque by default: a sticky header paints over the rows scrolling beneath it, and
        // neither the wrapper nor `TableHead` carries a background of its own. Tables that want
        // the page through them pass `bg-transparent`.
        'bg-background rounded-t-lg border-b-(length:--default-border-width)',
        sticky && 'sticky top-0 z-20',
        className
      )}
      style={{
        ...(style as React.CSSProperties),
        borderRightWidth: 'var(--scrollbar-width, 0px)',
        borderRightColor: 'transparent'
      }}
      {...props}
    />
  )
}

function TableBody({
  className,
  style,
  scrollable,
  maxHeight,
  ...props
}: React.ComponentProps<'div'> & { scrollable?: boolean; maxHeight?: number | string }) {
  const bodyRef = React.useRef<HTMLDivElement | null>(null)

  // Sync a CSS var on the parent table with the current vertical scrollbar width
  React.useLayoutEffect(() => {
    function updateScrollbarVar() {
      const current = bodyRef.current
      if (!current) return
      const scrollbarWidth = current.offsetWidth - current.clientWidth
      current.parentElement?.style.setProperty(
        '--scrollbar-width',
        `${Math.max(0, scrollbarWidth)}px`
      )
    }

    updateScrollbarVar()

    const current = bodyRef.current
    if (!current) return
    const resizeObserver = new ResizeObserver(() => updateScrollbarVar())
    resizeObserver.observe(current)

    return () => resizeObserver.disconnect()
  }, [])

  return (
    <div
      data-slot="table-body"
      role="rowgroup"
      className={cn(
        '[&>[data-slot=table-row]:last-child]:border-0',
        scrollable && 'overflow-y-auto',
        className
      )}
      ref={bodyRef}
      style={{
        ...((style as React.CSSProperties) || {}),
        maxHeight: scrollable ? maxHeight : undefined
      }}
      {...props}
    />
  )
}

function TableFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="table-footer"
      role="rowgroup"
      className={cn(
        'bg-muted/50 border-t-[0.5px] font-medium [&>[data-slot=table-row]:last-child]:border-0',
        className
      )}
      {...props}
    />
  )
}

function TableRow({
  className,
  style,
  isLoading,
  enableHover = false,
  onClick,
  ...props
}: React.ComponentProps<'div'> & { isLoading?: boolean; enableHover?: boolean }) {
  const rowStyle: React.CSSProperties = {
    gridTemplateColumns: 'var(--table-columns, 1fr)',
    gridAutoColumns: '1fr',
    gridAutoFlow: 'column'
  }

  if (isLoading) {
    return (
      <Skeleton
        role="row"
        data-slot="table-row"
        className={cn('h-10 w-full', className)}
        style={{ ...rowStyle, ...style }}
        {...props}
      />
    )
  }

  return (
    <div
      data-slot="table-row"
      role="row"
      className={cn(
        'data-[state=selected]:bg-muted group grid transition-colors',
        enableHover && 'hover:bg-muted/50',
        onClick && 'cursor-pointer',
        className
      )}
      style={{ ...rowStyle, ...style }}
      onClick={onClick}
      {...props}
    />
  )
}

function TableHead({
  className,
  sticky,
  ...props
}: React.ComponentProps<'div'> & { sticky?: 'left' | 'right' }) {
  return (
    <div
      data-slot="table-head"
      role="columnheader"
      className={cn(
        'text-secondary-foreground hover:text-foreground group/head relative flex h-10 items-center px-4 text-left align-middle text-xs whitespace-nowrap transition-colors [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
        getStickyCellClassName(sticky, true),
        className
      )}
      {...props}
    />
  )
}

function TableCell({
  className,
  sticky,
  ...props
}: React.ComponentProps<'div'> & { sticky?: 'left' | 'right' }) {
  return (
    <div
      data-slot="table-cell"
      role="cell"
      className={cn(
        'relative px-4 py-3 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
        getStickyCellClassName(sticky),
        className
      )}
      {...props}
    />
  )
}

function getStickyCellClassName(sticky?: 'left' | 'right', isHeader?: boolean) {
  if (!sticky) return undefined
  return cn(
    'sticky from-transparent z-10 via-card to-card',
    'after:pointer-events-none group-hover:after:absolute group-hover:after:inset-0 group-hover:after:bg-card',
    {
      'z-30': isHeader,
      'left-0 not-group-hover:bg-gradient-to-l group-hover:bg-card': sticky === 'left',
      'right-0 not-group-hover:bg-gradient-to-r group-hover:bg-card': sticky === 'right'
    }
  )
}

function TableCaption({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="table-caption"
      className={cn('text-muted-foreground mt-4 text-sm', className)}
      {...props}
    />
  )
}

const SORT_CLASS_MAP = {
  asc: 'scale-y-100',
  desc: '-scale-y-100 -translate-y-[2px]'
}

export function TableSortIcon({
  isSorted,
  nextSortingOrder,
  className,
  isSortedClassName
}: {
  isSorted: false | SortDirection
  nextSortingOrder: false | SortDirection
  className?: string
  isSortedClassName?: string
}) {
  function renderSortClass() {
    if (isSorted) {
      return SORT_CLASS_MAP[isSorted]
    }

    return SORT_CLASS_MAP[nextSortingOrder as SortDirection]
  }

  return (
    <span
      className={cn(
        'text-secondary-foreground inline-block origin-center text-xs opacity-0 transition-all group-hover/head:opacity-100',
        isSorted && 'text-secondary-foreground group-hover/head:text-foreground opacity-100',
        renderSortClass(),
        className,
        isSorted && isSortedClassName
      )}
    >
      ↑
    </span>
  )
}

type VirtualItem = {
  key?: React.Key
  index: number
  start: number
  size: number
}

function VirtualTableBody({
  height,
  className,
  style,
  children,
  parentRef,
  viewportHeight,
  ...props
}: React.ComponentProps<'div'> & {
  height: number
  parentRef?: React.Ref<HTMLDivElement>
  viewportHeight?: string
}) {
  const isPercentageHeight =
    typeof viewportHeight === 'string' && viewportHeight.trim().endsWith('%')

  const localRef = React.useRef<HTMLDivElement | null>(null)

  // Set the same scrollbar width var used by the header for virtualized tables
  React.useLayoutEffect(() => {
    function updateScrollbarVar() {
      const current = localRef.current
      if (!current) return
      const scrollbarWidth = current.offsetWidth - current.clientWidth
      current
        .closest<HTMLElement>('[data-slot="table"]')
        ?.style.setProperty('--scrollbar-width', `${Math.max(0, scrollbarWidth)}px`)
    }

    updateScrollbarVar()

    const current = localRef.current
    if (!current) return
    const resizeObserver = new ResizeObserver(() => updateScrollbarVar())
    resizeObserver.observe(current)

    return () => resizeObserver.disconnect()
  }, [])

  // Merge provided ref with our local ref
  const setRefs = (el: HTMLDivElement | null) => {
    localRef.current = el
    if (!parentRef) return
    if (typeof parentRef === 'function') {
      parentRef(el)
    } else if (typeof parentRef === 'object') {
      // oxlint-disable-next-line typescript/no-explicit-any
      ;(parentRef as any).current = el
    }
  }
  return (
    <div
      data-slot="table-virtual-body"
      role="rowgroup"
      ref={setRefs}
      // `overflow-x-hidden`: `overflow-y-auto` alone computes overflow-x to `auto`,
      // making this a nested horizontal scroll container that latches wheel gestures
      // away from the table container's real x-scroller — columns then never move.
      className={cn('relative min-h-0 overflow-x-hidden overflow-y-auto', className, {
        'h-full flex-1': isPercentageHeight
      })}
      style={{
        ...(style as React.CSSProperties),
        height: viewportHeight ?? '400px'
      }}
      {...props}
    >
      <div style={{ height: `${height}px`, position: 'relative' }}>{children}</div>
    </div>
  )
}

function VirtualTableRow({
  virtualItem,
  className,
  style,
  isLoading,
  measureRef,
  expandedContent,
  enableHover = true,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  virtualItem?: VirtualItem
  isLoading?: boolean
  measureRef?: (node: Element | null) => void
  expandedContent?: React.ReactNode
  enableHover?: boolean
}) {
  const rowClassName = cn(
    'data-[state=selected]:bg-muted grid',
    enableHover && 'hover:bg-muted/50',
    className
  )
  const gridStyle: React.CSSProperties = {
    gridTemplateColumns: 'var(--table-columns, 1fr)',
    gridAutoColumns: '1fr',
    gridAutoFlow: 'column'
  }

  const positionStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    transform: `translateY(${virtualItem?.start ?? 0}px)`
  }

  if (isLoading) {
    return (
      <Skeleton
        role="row"
        data-slot="table-virtual-row"
        className={cn('w-full', className)}
        style={{ ...positionStyle, height: `${virtualItem?.size ?? 48}px`, ...gridStyle, ...style }}
        {...props}
      />
    )
  }

  if (measureRef) {
    return (
      <div ref={measureRef} data-index={virtualItem?.index} style={positionStyle}>
        <div
          data-slot="table-virtual-row"
          role="row"
          className={rowClassName}
          style={{ ...gridStyle, ...style }}
          {...props}
        >
          {children}
        </div>
        {expandedContent}
      </div>
    )
  }

  return (
    <div
      data-slot="table-virtual-row"
      role="row"
      className={rowClassName}
      style={{ ...positionStyle, height: `${virtualItem?.size ?? 48}px`, ...gridStyle, ...style }}
      {...props}
    >
      {children}
    </div>
  )
}

export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  VirtualTableBody,
  VirtualTableRow
}
